import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '../components/ui';
import { zh } from '../i18n';
import type { ToolItem } from '../stream/streamReducer';
import { ApprovalCard, type ApprovalResolveInput } from './ApprovalCard';
import { Markdown } from './Markdown';
import { ToolCard } from './ToolCard';
import { TurnFooter } from './TurnFooter';
import type { RenderRow } from './grouping';

/**
 * The transcript (SPEC §10).
 *
 * - settled text blocks render as memoized markdown, the live tail as plain
 *   text: parsing a growing block on every delta is the O(n²) trap;
 * - the list virtualizes past 200 rows;
 * - auto-stick only within 80 px of the bottom, otherwise the 「↓ 有新訊息」
 *   pill — yanking the viewport away from something a user is reading is worse
 *   than making them tap once.
 */

const VIRTUALIZE_THRESHOLD = 200;
const STICK_PX = 80;

export interface ChatTranscriptProps {
  readonly rows: readonly RenderRow[];
  readonly toolsByTurn: Readonly<Record<string, readonly ToolItem[]>>;
  readonly onResolveApproval: (approvalId: string, input: ApprovalResolveInput) => void;
  readonly resolvingApprovalId: string | null;
  readonly maxTurns: number | null;
  readonly maxBudgetUsd: number | null;
  readonly budgetsEnforced: boolean;
  readonly onLoadOlder: (() => void) | null;
  readonly loadingOlder: boolean;
}

type RowProps = Pick<
  ChatTranscriptProps,
  | 'toolsByTurn'
  | 'onResolveApproval'
  | 'resolvingApprovalId'
  | 'maxTurns'
  | 'maxBudgetUsd'
  | 'budgetsEnforced'
> & { row: RenderRow };

function Row({
  row,
  toolsByTurn,
  onResolveApproval,
  resolvingApprovalId,
  maxTurns,
  maxBudgetUsd,
  budgetsEnforced,
}: RowProps): JSX.Element | null {
  switch (row.kind) {
    case 'message':
      if (row.item.role === 'user') {
        return (
          <div className="bubble">
            <span className="sr-only">{zh.chat.userLabel}</span>
            {row.item.content}
          </div>
        );
      }
      if (row.item.role === 'system') {
        return (
          <div className="system-note">
            <span className="sr-only">{zh.chat.systemLabel}</span>
            {row.item.content}
          </div>
        );
      }
      return (
        <div className="assistant">
          <span className="sr-only">{zh.chat.assistantLabel}</span>
          <Markdown source={row.item.content} />
        </div>
      );

    case 'text':
      return (
        <div className="assistant">
          {row.item.done ? (
            <Markdown source={row.item.text} />
          ) : (
            <div className="assistant__tail">{row.item.text}</div>
          )}
        </div>
      );

    case 'tools':
      return <ToolCard items={row.items} />;

    case 'approval':
      return (
        <ApprovalCard
          item={row.item}
          busy={resolvingApprovalId === row.item.approval.id}
          onResolve={(input) => onResolveApproval(row.item.approval.id, input)}
        />
      );

    case 'turnFooter':
      return (
        <TurnFooter
          turn={row.turn}
          tools={toolsByTurn[row.turn.id] ?? []}
          maxTurns={maxTurns}
          maxBudgetUsd={maxBudgetUsd}
          budgetsEnforced={budgetsEnforced}
        />
      );

    case 'thinking':
      return <div className="assistant assistant--thinking">{zh.chat.thinking}</div>;

    default:
      return null;
  }
}

export function ChatTranscript({
  rows,
  toolsByTurn,
  onResolveApproval,
  resolvingApprovalId,
  maxTurns,
  maxBudgetUsd,
  budgetsEnforced,
  onLoadOlder,
  loadingOlder,
}: ChatTranscriptProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastRowsRef = useRef<readonly RenderRow[] | null>(null);
  const [stuck, setStuck] = useState(true);
  const [hasNew, setHasNew] = useState(false);
  const virtualize = rows.length > VIRTUALIZE_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: virtualize ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 110,
    overscan: 8,
    getItemKey: (index) => rows[index]?.key ?? index,
  });

  const scrollToBottom = (): void => {
    const element = scrollRef.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
    setStuck(true);
    setHasNew(false);
  };

  useLayoutEffect(() => {
    const rowsChanged = lastRowsRef.current !== rows;
    lastRowsRef.current = rows;
    if (stuck) scrollToBottom();
    // Only content arriving raises the pill. Scrolling up must not announce
    // messages the reader has already seen.
    else if (rowsChanged) setHasNew(true);
  }, [rows, stuck]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const onScroll = (): void => {
      const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
      const near = distance <= STICK_PX;
      setStuck(near);
      if (near) setHasNew(false);
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, []);

  const rowProps = {
    toolsByTurn,
    onResolveApproval,
    resolvingApprovalId,
    maxTurns,
    maxBudgetUsd,
    budgetsEnforced,
  };

  return (
    <>
      <div className="transcript" ref={scrollRef}>
        {onLoadOlder ? (
          <Button variant="ghost" small block disabled={loadingOlder} onClick={onLoadOlder}>
            {loadingOlder ? zh.app.loading : zh.threads.loadMore}
          </Button>
        ) : null}

        {virtualize ? (
          <div
            className="transcript__virtual"
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index];
              if (!row) return null;
              return (
                <div
                  key={virtualRow.key}
                  className="transcript__virtual-item"
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <Row row={row} {...rowProps} />
                </div>
              );
            })}
          </div>
        ) : (
          rows.map((row) => (
            <div key={row.key}>
              <Row row={row} {...rowProps} />
            </div>
          ))
        )}
      </div>
      {hasNew ? (
        <button type="button" className="jump-pill" onClick={scrollToBottom}>
          {zh.chat.newMessages}
        </button>
      ) : null}
    </>
  );
}
